import React, {useMemo} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {Alert, FlexColumn, FlexRow, Frame, Modal, Text} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import type {InvoicedBillToWarningModal_viewer$data as InvoicedBillToWarningModal_viewer} from './__generated__/InvoicedBillToWarningModal_viewer.graphql';
import ProjectIndicator from '../../../shared/components/project-indicator/ProjectIndicator';

type InvoicedBillToWarningModalProps = {
	billToId: string;
	clientId?: string;
	closeModal: () => void;
	viewer: InvoicedBillToWarningModal_viewer;
};

const InvoicedBillToWarningModal = ({billToId, clientId, closeModal, viewer}: InvoicedBillToWarningModalProps) => {
	const {formatMessage} = useIntl();

	const projects = useMemo(() => {
		const filteredInvoices = viewer.invoicesV2?.edges
			?.map(invoice => invoice?.node)
			?.filter(invoice => invoice?.billTo?.id === billToId && (!clientId || invoice?.client?.id === clientId));
		const allProjects = filteredInvoices?.flatMap(invoice => invoice?.projects);
		// Remove duplicates
		return allProjects?.filter((project, index) => {
			return allProjects?.findIndex(x => x?.id === project?.id) === index;
		});
	}, [viewer.invoicesV2]);

	let modalTitle, modalAlert, modalDesc1, modalDesc2;

	if (clientId) {
		modalTitle = formatMessage({id: 'settings_finance.bill_to.unassociate_client'});
		modalAlert = formatMessage({id: 'settings_finance.bill_to.unassociate_client.invoiced.alert'});
		modalDesc1 = formatMessage({id: 'settings_finance.bill_to.unassociate_client.invoiced.desc1'});
		modalDesc2 = formatMessage({id: 'settings_finance.bill_to.unassociate_client.invoiced.desc2'});
	} else {
		modalTitle = formatMessage({id: 'settings_finance.bill_to.remove'});
		modalAlert = formatMessage({id: 'settings_finance.remove_bill_to_entity_alert'});
		modalDesc1 = formatMessage({id: 'settings_finance.bill_to_entity_warning_desc_1'});
		modalDesc2 = formatMessage({id: 'settings_finance.bill_to_entity_warning_desc_2'});
	}

	return (
		<Modal onCloseModal={closeModal}>
			<Modal.Title>{modalTitle}</Modal.Title>
			<Modal.Content>
				<Alert text={modalAlert} type={Alert.TYPE.ERROR} />
				<Text>{modalDesc1}</Text>
				<Frame>
					<FlexColumn gap={'m'}>
						{projects?.map(project =>
							project ? (
								<FlexRow gap={'m'}>
									<ProjectIndicator project={project} />
									<Text ellipsis>{project.name}</Text>
								</FlexRow>
							) : null
						)}
					</FlexColumn>
				</Frame>
				<Text>{modalDesc2}</Text>
			</Modal.Content>
			<Modal.Footer>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					{formatMessage({id: 'common.close'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const InvoicedBillToWarningModalQuery = graphql`
	query InvoicedBillToWarningModalQuery {
		viewer {
			actualPersonId
			component(name: "InvoicedBillToWarningModal")
			...InvoicedBillToWarningModal_viewer
		}
	}
`;

export default createFragmentContainer(InvoicedBillToWarningModal, {
	viewer: graphql`
		fragment InvoicedBillToWarningModal_viewer on Viewer {
			id
			invoicesV2(first: 100000) {
				edges {
					node {
						id
						billTo {
							id
						}
						client {
							id
						}
						projects {
							id
							name
							projectColor
							companyProjectId
							...ProjectIndicator_project
						}
					}
				}
			}
		}
	`,
});
